/**
 * Subtitle typography, colors, and motion presets (preview + export payload).
 */

import { clampSpokenWordLimit, SPOKEN_WORD_HIGHLIGHT_DEFAULT } from '@/lib/subtitles/spokenWordWindow';

export const SUBTITLE_FONT_OPTIONS = [
  { id: 'Noto Sans Devanagari', label: 'Noto Sans Devanagari (Hindi)', stack: '"Noto Sans Devanagari", "Noto Sans", sans-serif' },
  { id: 'Arial', label: 'Arial', stack: 'Arial, Helvetica, sans-serif' },
  { id: 'Helvetica', label: 'Helvetica', stack: 'Helvetica, Arial, sans-serif' },
  { id: 'Georgia', label: 'Georgia', stack: 'Georgia, "Times New Roman", serif' },
  { id: 'Times New Roman', label: 'Times New Roman', stack: '"Times New Roman", Times, serif' },
  { id: 'Verdana', label: 'Verdana', stack: 'Verdana, Geneva, sans-serif' },
  { id: 'Tahoma', label: 'Tahoma', stack: 'Tahoma, Geneva, sans-serif' },
  { id: 'Trebuchet MS', label: 'Trebuchet', stack: '"Trebuchet MS", Helvetica, sans-serif' },
  { id: 'Impact', label: 'Impact', stack: 'Impact, Haettenschweiler, sans-serif' },
  { id: 'Courier New', label: 'Courier', stack: '"Courier New", Courier, monospace' },
  { id: 'Comic Sans MS', label: 'Comic Sans', stack: '"Comic Sans MS", cursive, sans-serif' },
  { id: 'Palatino Linotype', label: 'Palatino', stack: '"Palatino Linotype", Palatino, serif' },
  { id: 'Lucida Console', label: 'Lucida Console', stack: '"Lucida Console", Monaco, monospace' },
];

export const SUBTITLE_COLOR_PRESETS = [
  '#ffffff',
  '#ffff00',
  '#ffd700',
  '#ff6b6b',
  '#ff8c42',
  '#c084fc',
  '#60a5fa',
  '#34d399',
  '#f472b6',
  '#e2e8f0',
  '#1e293b',
  '#000000',
];

export const SUBTITLE_ANIMATION_OPTIONS = [
  { id: 'none', label: 'None' },
  { id: 'blink', label: 'Blink' },
  { id: 'wave', label: 'Wave' },
  { id: 'pulse', label: 'Pulse' },
  { id: 'glow', label: 'Glow pulse' },
  { id: 'bounce', label: 'Bounce' },
  { id: 'shake', label: 'Shake' },
  { id: 'slide-up', label: 'Slide up' },
  { id: 'fade', label: 'Fade in/out' },
  { id: 'pop', label: 'Pop' },
];

const FONT_IDS = new Set(SUBTITLE_FONT_OPTIONS.map((f) => f.id));
const ANIMATION_IDS = new Set(SUBTITLE_ANIMATION_OPTIONS.map((a) => a.id));

function clampNum(n, lo, hi, fb) {
  const v = Number(n);
  return Number.isFinite(v) ? Math.min(hi, Math.max(lo, v)) : fb;
}

function normalizeHexColor(raw, fallback) {
  const s = typeof raw === 'string' ? raw.trim() : '';
  if (/^#[0-9a-fA-F]{3,8}$/.test(s)) return s.length === 4 ? expandShortHex(s) : s.slice(0, 7);
  return fallback;
}

function expandShortHex(hex) {
  const h = hex.slice(1);
  if (h.length === 3) {
    return `#${h[0]}${h[0]}${h[1]}${h[1]}${h[2]}${h[2]}`;
  }
  return hex;
}

export function hexToRgba(hex, alpha = 1) {
  const c = normalizeHexColor(hex, '#000000').slice(1);
  const r = parseInt(c.slice(0, 2), 16);
  const g = parseInt(c.slice(2, 4), 16);
  const b = parseInt(c.slice(4, 6), 16);
  const a = clampNum(alpha, 0, 1, 1);
  return `rgba(${r}, ${g}, ${b}, ${a})`;
}

export function subtitleFontStack(fontFamily) {
  const id = FONT_IDS.has(fontFamily) ? fontFamily : 'Arial';
  return SUBTITLE_FONT_OPTIONS.find((f) => f.id === id)?.stack || 'Arial, Helvetica, sans-serif';
}

/** Use a Devanagari-capable font when Hindi script is present (matches export burn-in). */
export function resolveSubtitleFontFamily(text, preferred = 'Arial') {
  if (/[\u0900-\u097F]/.test(String(text || ''))) {
    return 'Noto Sans Devanagari';
  }
  const pref = String(preferred || 'Arial').trim();
  return FONT_IDS.has(pref) ? pref : 'Arial';
}

export function normalizeSubtitleDisplayText(text) {
  return String(text || '').normalize('NFC').trim();
}

/** Karaoke line: a few uppercase words, with the spoken word on a yellow box. */
function karaokeSubtitleFields(src, d) {
  const rawWindow = src.spokenWordWindow ?? src.spoken_word_window;
  const rawHighlight = String(src.spokenWordHighlightColor ?? src.spoken_word_highlight_color ?? '').trim().toLowerCase();
  const neverChosen = rawWindow === false && (!rawHighlight || rawHighlight === '#fde047');
  const spokenWordWindow = neverChosen || rawWindow == null
    ? d.spokenWordWindow !== false
    : Boolean(rawWindow);
  return {
    spokenWordWindow,
    spokenWordLimit: clampSpokenWordLimit(src.spokenWordLimit ?? src.spoken_word_limit ?? d.spokenWordLimit),
    spokenWordHighlightColor: normalizeHexColor(
      neverChosen ? SPOKEN_WORD_HIGHLIGHT_DEFAULT : (src.spokenWordHighlightColor ?? src.spoken_word_highlight_color),
      d.spokenWordHighlightColor || SPOKEN_WORD_HIGHLIGHT_DEFAULT,
    ),
  };
}

/** Merge extended style fields onto a normalized base style object. */
export function applyExtendedSubtitleStyleFields(base, raw) {
  const d = base || {};
  const src = raw && typeof raw === 'object' ? raw : {};
  const fontFamilyRaw = String(src.fontFamily ?? src.font_family ?? d.fontFamily ?? 'Arial').trim();
  const fontFamily = FONT_IDS.has(fontFamilyRaw) ? fontFamilyRaw : 'Arial';
  const animationRaw = String(src.animation ?? src.effect ?? d.animation ?? 'none').trim().toLowerCase();
  const animation = ANIMATION_IDS.has(animationRaw) ? animationRaw : 'none';
  return {
    ...d,
    fontFamily,
    italic: Boolean(src.italic ?? d.italic),
    underline: Boolean(src.underline ?? d.underline),
    letterSpacing: Math.round(clampNum(src.letterSpacing ?? src.letter_spacing, -2, 12, d.letterSpacing ?? 0)),
    shadowColor: normalizeHexColor(src.shadowColor ?? src.shadow_color, d.shadowColor || '#ffff00'),
    shadowBlur: clampNum(src.shadowBlur ?? src.shadow_blur, 0, 24, d.shadowBlur ?? 0),
    backgroundColor: normalizeHexColor(src.backgroundColor ?? src.background_color, d.backgroundColor || '#000000'),
    backgroundOpacity: clampNum(src.backgroundOpacity ?? src.background_opacity, 0, 1, d.backgroundOpacity ?? 0),
    animation,
    ...karaokeSubtitleFields(src, d),
  };
}

export function subtitleStyleToExportPayload(styleNow, layerNow, exportAnchors) {
  return {
    anchor_x: exportAnchors.anchorX,
    anchor_y: exportAnchors.anchorY,
    font_size: styleNow.fontSize,
    font_family: styleNow.fontFamily || 'Arial',
    color: styleNow.color,
    outline_width: styleNow.outlineWidth,
    outline_color: styleNow.outlineColor,
    bold: styleNow.bold,
    italic: styleNow.italic,
    underline: styleNow.underline,
    letter_spacing: styleNow.letterSpacing,
    shadow_color: styleNow.shadowColor,
    shadow_blur: styleNow.shadowBlur,
    background_color: styleNow.backgroundColor,
    background_opacity: styleNow.backgroundOpacity,
    animation: styleNow.animation || 'none',
    spoken_word_window: Boolean(styleNow.spokenWordWindow),
    spoken_word_limit: clampSpokenWordLimit(styleNow.spokenWordLimit),
    spoken_word_highlight_color: styleNow.spokenWordHighlightColor || SPOKEN_WORD_HIGHLIGHT_DEFAULT,
    layer: layerNow,
    rotation: layerNow.rotation,
    width: layerNow.width,
    height: layerNow.height,
    text_align: layerNow.textAlign,
  };
}

export function subtitleAnimationClassName(animation) {
  const id = String(animation || 'none').toLowerCase();
  if (!id || id === 'none') return '';
  if (id === 'glow') return 'subtitle-anim-glow';
  return `subtitle-anim-${id.replace(/[^a-z0-9-]/g, '')}`;
}

export function subtitleTextContainerStyle(style, layout, text = '') {
  const bgOpacity = clampNum(style?.backgroundOpacity, 0, 1, 0);
  const hasBg = bgOpacity > 0.02;
  const fontFamily = resolveSubtitleFontFamily(text, style?.fontFamily);
  return {
    color: style?.color || '#ffffff',
    fontSize: `${layout.fontPxExact}px`,
    fontFamily: subtitleFontStack(fontFamily),
    fontWeight: style?.spokenWordWindow || style?.bold ? 800 : 600,
    fontStyle: style?.italic ? 'italic' : 'normal',
    textDecoration: style?.underline ? 'underline' : 'none',
    letterSpacing: style?.letterSpacing ? `${style.letterSpacing}px` : undefined,
    textShadow: layout.shadow,
    lineHeight: 1.28,
    textAlign: layout.textAlign,
    ...(hasBg
      ? {
        backgroundColor: hexToRgba(style.backgroundColor, bgOpacity),
        borderRadius: '6px',
        padding: '4px 10px',
      }
      : {}),
  };
}
